/**
 * Helper for managing AbortControllers in RTK Query endpoints to cancel previous requests.
 *
 * This is needed when:
 * 1. Different query params create different cache keys (RTK Query won't auto-cancel)
 * 2. You want to prevent unnecessary server load from abandoned requests
 * 3. Users can trigger new queries before previous ones complete (e.g., Apply Filters button)
 *
 * @example
 * import { createAbortableQuery } from 'utils/helpers/abortPreviousRequest';
 *
 * const activeRequests = new Map();
 *
 * myEndpoint: query({
 *   queryFn: createAbortableQuery(
 *     activeRequests,
 *     'myEndpoint',
 *     async (args, api, extraOptions, baseQuery) => {
 *       return baseQuery({ url: '/my-endpoint', body: args }, api, extraOptions);
 *     }
 *   ),
 * }),
 */

/**
 * Creates a cache key for tracking requests.
 * Uses endpoint name + stringified args to uniquely identify each query.
 */
function getCacheKey(endpointName, args) {
  return `${endpointName}-${JSON.stringify(args)}`;
}

/**
 * Creates an abortable queryFn that cancels previous requests with the same cache key.
 *
 * @param {Map} requestMap - Map to store active AbortControllers (shared across calls)
 * @param {string} endpointName - Name of the endpoint (for cache key generation)
 * @param {Function} queryFn - The actual query function to execute
 * @returns {Function} Wrapped queryFn that handles abort logic
 */
export function createAbortableQuery(requestMap, endpointName, queryFn) {
  return async (args, api, extraOptions, baseQuery) => {
    const cacheKey = getCacheKey(endpointName, args);

    // Cancel previous request for this specific query if it exists
    if (requestMap.has(cacheKey)) {
      const previousController = requestMap.get(cacheKey);
      previousController.abort();
      requestMap.delete(cacheKey);
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    requestMap.set(cacheKey, controller);

    try {
      // Execute the query with the abort signal
      const result = await queryFn(args, api, extraOptions, baseQuery, controller.signal);

      // Clean up on success
      requestMap.delete(cacheKey);
      return result;
    } catch (error) {
      // Clean up on error
      requestMap.delete(cacheKey);
      throw error;
    }
  };
}

/**
 * Alternative: Simpler version that cancels ALL previous requests for an endpoint
 * (regardless of params). Use when you want aggressive cancellation.
 *
 * @param {Object} controllerRef - Object with a 'current' property to store the controller
 * @param {Function} queryFn - The actual query function to execute
 * @returns {Function} Wrapped queryFn that handles abort logic
 */
export function createSingleAbortableQuery(controllerRef, queryFn) {
  return async (args, api, extraOptions, baseQuery) => {
    // Cancel previous request if it exists
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    // Create new AbortController
    // eslint-disable-next-line no-param-reassign
    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;

    try {
      const result = await queryFn(args, api, extraOptions, baseQuery, signal);
      // eslint-disable-next-line no-param-reassign
      controllerRef.current = null;
      return result;
    } catch (error) {
      // eslint-disable-next-line no-param-reassign
      controllerRef.current = null;
      throw error;
    }
  };
}
